import { router, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import { BuildingIcon, CheckIcon, ChevronsUpDownIcon } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Badge } from "@/components/ui/badge"

interface UserOrganization {
  id: number
  name: string
  role: string
}

interface OrganizationSwitcherPageProps {
  organization: { id: number; name: string } | null
  user_organizations: UserOrganization[]
  auth: { user: { organization_role: string } | null }
  [key: string]: unknown
}

export function OrganizationSwitcher() {
  const { isMobile } = useSidebar()
  const { t } = useTranslation('common')
  const { props } = usePage<OrganizationSwitcherPageProps>()

  const currentOrg = props.organization
  const userOrganizations: UserOrganization[] = props.user_organizations || []

  if (!currentOrg || userOrganizations.length <= 1) {
    return null
  }

  const handleSwitch = (orgId: number) => {
    if (orgId === currentOrg.id) return
    router.post('/organization/switch', { organization_id: orgId }, {
      preserveScroll: false,
    })
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <BuildingIcon className="size-4" />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{currentOrg.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {getRoleLabel(props.auth?.user?.organization_role, t)}
                </span>
              </div>
              <ChevronsUpDownIcon className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="end"
            side={isMobile ? "top" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {t('orgSwitcher.title')}
            </DropdownMenuLabel>
            {userOrganizations.map((org) => (
              <DropdownMenuItem
                key={org.id}
                onClick={() => handleSwitch(org.id)}
                className="gap-2 p-2"
              >
                <div className="flex size-6 items-center justify-center rounded-sm border">
                  <BuildingIcon className="size-4 shrink-0" />
                </div>
                <span className="flex-1 truncate">{org.name}</span>
                {org.role === 'external' && (
                  <Badge variant="outline" className="text-xs ml-1">
                    {t('orgSwitcher.external')}
                  </Badge>
                )}
                {org.id === currentOrg.id && (
                  <CheckIcon className="size-4 ml-auto shrink-0" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

function getRoleLabel(role: string | undefined, t: (key: string) => string): string {
  switch (role) {
    case 'admin': return t('orgSwitcher.roleAdmin')
    case 'member': return t('orgSwitcher.roleMember')
    case 'external': return t('orgSwitcher.roleExternal')
    default: return t('orgSwitcher.roleMember')
  }
}
